import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { HiDotsVertical } from 'react-icons/hi';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Text } from 'components/Elements';
import storage from 'utils/storage';

const generateChartData = (base = 5, points = 7) => {
  return Array.from({ length: points }, (_, i) => ({
    name: `Day ${i + 1}`,
    value: Math.max(
      1,
      base + Math.round(Math.sin(i * 0.5) * 2 + Math.random() * 2)
    ),
  }));
};

export const ApprovalRequired = ({ unapprovedWithdraws }) => {
  const navigate = useNavigate();
  const [trend, setTrend] = useState('neutral');
  const [percentageChange, setPercentageChange] = useState(0);
  const [view, setView] = useState('weekly');
  const [chartData, setChartData] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const analyticsData = storage.getAnalyticsData() || {};
    const previousCount = analyticsData.unapprovedWithdraws || 0;

    const change =
      previousCount === 0
        ? 100
        : ((unapprovedWithdraws - previousCount) / previousCount) * 100;

    const rounded = Number(change.toFixed(1));
    setPercentageChange(rounded);

    setTrend(rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral');

    storage.setAnalyticsData({
      ...analyticsData,
      unapprovedWithdraws,
    });
  }, [unapprovedWithdraws]);

  useEffect(() => {
    const base = unapprovedWithdraws;
    const points = view === 'weekly' ? 7 : 30;
    setChartData(generateChartData(base, points));
  }, [view, unapprovedWithdraws]);

  const colorMap = {
    up: '#facc15', // yellow
    down: '#f87171', // red
    neutral: '#a1a1aa', // gray
  };

  const TrendIcon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : null;

  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate('/app/transactions?preview=unapproved_withdraws')}
      className="hover:cursor-pointer transition-all duration-300 rounded-3xl w-full max-w-sm mx-auto bg-gradient-to-br from-[#1e3c72]/30 to-[#2a5298]/30 backdrop-blur-lg border border-white/20 shadow-lg"
    >
      <div className="pt-6 px-6 flex justify-between items-center mb-2">
        <div className="text-white/80 font-medium text-xs">Pending Withdrawals</div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
            className="text-white/60 hover:text-white"
          >
            <HiDotsVertical size={18} />
          </button>
          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute top-6 right-0 bg-white/10 backdrop-blur-md border border-white/10 rounded-md shadow z-10"
            >
              {['weekly', 'monthly'].map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    setView(option);
                    setMenuOpen(false);
                  }}
                  className={`block px-4 py-2 text-sm w-full text-left hover:bg-white/10 ${
                    view === option ? 'text-white font-semibold' : 'text-white/70'
                  }`}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="h-20 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="withdrawTrend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colorMap[trend]} stopOpacity={0.25} />
                <stop offset="100%" stopColor={colorMap[trend]} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Tooltip
              contentStyle={{
                backgroundColor: '#0f172a',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
              }}
              labelStyle={{ display: 'none' }}
              cursor={false}
            />
            <XAxis dataKey="name" hide />
            <YAxis hide />
            <Area
              type="monotone"
              dataKey="value"
              stroke={colorMap[trend]}
              fill="url(#withdrawTrend)"
              strokeWidth={2}
              activeDot={{ r: 5 }}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="px-6 flex items-center justify-between pb-6">
        <Text variant="title" className="text-white text-5xl font-bold">
          {unapprovedWithdraws}
        </Text>

        {TrendIcon && (
          <div
            className={`flex flex-row items-center space-x-1 text-sm font-medium ${
              trend === 'up' ? 'text-yellow-400' : 'text-red-400'
            }`}
          >
            <TrendIcon size={16} />
            <span>{Math.abs(percentageChange)}%</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};
